import { useState } from "react";
import { NavLink, Outlet, Link } from "react-router-dom";
import { LOGO_URL } from "@/lib/api";
import { Home, Images, Phone, Menu, X, ShieldCheck } from "lucide-react";

const navItems = [
  { to: "/", label: "Home", icon: Home, testId: "pub-nav-home" },
  { to: "/gallery", label: "Gallery", icon: Images, testId: "pub-nav-gallery" },
  { to: "/contact", label: "Contact Us", icon: Phone, testId: "pub-nav-contact" },
];

function Brand() {
  return (
    <div className="flex items-center gap-3">
      <img src={LOGO_URL} alt="JAI JAGDAMBA LOGISTICS" className="w-11 h-11 object-contain rounded bg-white p-0.5" />
      <div>
        <div className="font-['Outfit'] font-extrabold text-lg leading-tight text-white tracking-tight">JAI JAGDAMBA</div>
        <div className="text-[10px] tracking-[0.3em] text-orange-400 font-bold">LOGISTICS</div>
      </div>
    </div>
  );
}

function NavLinks({ onNavigate }) {
  return (
    <nav className="flex-1 px-4 py-6 space-y-1">
      {navItems.map(({ to, label, icon: Icon, testId }) => (
        <NavLink
          key={to}
          to={to}
          end={to === "/"}
          onClick={onNavigate}
          data-testid={testId}
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-3 rounded-md text-base font-semibold transition-colors duration-200 ${
              isActive
                ? "bg-slate-800 text-white border-r-4 border-orange-500"
                : "text-slate-400 hover:text-white hover:bg-slate-800/60"
            }`
          }
        >
          <Icon size={20} />
          {label}
        </NavLink>
      ))}
    </nav>
  );
}

function PortalButton({ onNavigate }) {
  return (
    <div className="p-4 border-t border-slate-800">
      <Link
        to="/portal"
        onClick={onNavigate}
        data-testid="pub-portal-btn"
        className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-md border border-orange-500/50 text-orange-400 font-bold text-sm hover:bg-orange-500 hover:text-white transition-colors duration-200"
      >
        <ShieldCheck size={18} />
        Admin / Staff Portal
      </Link>
    </div>
  );
}

export default function PublicLayout() {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  return (
    <div className="min-h-screen bg-slate-50 font-['Plus_Jakarta_Sans']" data-testid="public-layout">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 w-72 bg-slate-900 z-50 hidden lg:flex flex-col">
        <div className="px-6 py-6 border-b border-slate-800">
          <Brand />
        </div>
        <NavLinks />
        <PortalButton />
      </aside>

      {/* Mobile top bar */}
      <header className="sticky top-0 z-40 w-full h-16 bg-slate-900 flex items-center justify-between px-4 lg:hidden">
        <Brand />
        <button onClick={() => setOpen(true)} className="text-white p-2" aria-label="Open menu" data-testid="pub-hamburger-btn">
          <Menu size={26} />
        </button>
      </header>

      {/* Mobile drawer */}
      <div className={`fixed inset-0 z-50 lg:hidden transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
        <div className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm" onClick={close} />
        <div className={`absolute inset-y-0 left-0 w-72 bg-slate-900 flex flex-col transform transition-transform duration-300 ${open ? "translate-x-0" : "-translate-x-full"}`} data-testid="pub-mobile-drawer">
          <div className="px-6 py-5 border-b border-slate-800 flex items-center justify-between">
            <Brand />
            <button onClick={close} className="text-slate-400 p-1" aria-label="Close menu" data-testid="pub-drawer-close-btn">
              <X size={22} />
            </button>
          </div>
          <NavLinks onNavigate={close} />
          <PortalButton onNavigate={close} />
        </div>
      </div>

      {/* Main content */}
      <main className="lg:ml-72 min-h-screen flex flex-col">
        <Outlet />
        <footer className="mt-auto bg-slate-900 text-slate-400 px-6 lg:px-16 py-8 text-sm flex flex-col sm:flex-row items-center justify-between gap-3">
          <span>© {new Date().getFullYear()} JAI JAGDAMBA LOGISTICS. All rights reserved.</span>
          <span className="text-slate-500">Reliable Logistics Across India</span>
        </footer>
      </main>
    </div>
  );
}
